
import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Heart } from "lucide-react";
import { BubbleCareAnimation } from "./BubbleCareAnimation";
import { useIsMobile } from "@/hooks/use-mobile";

const Hero = () => {
  const isMobile = useIsMobile();
  
  return (
    <section className="relative pt-32 pb-20 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50/80 to-white z-0"></div>
      <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] bg-center opacity-5 z-0"></div>
      
      <div className="container-custom relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 tracking-tight text-balance">
              Connect Your Loved One's Care Network with BubbleCare
            </h1>
            
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto text-balance">
              Create your BubbleCare bubble to connect caregivers, family members, and volunteers around your loved one. Book professional services, engage family members, and access the volunteer community all in one place.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <Button asChild size="lg" className="btn-primary">
                <Link to="/bubble">
                  Create Your Bubble <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              
              <Button asChild variant="outline" size="lg" className="btn-secondary">
                <Link to="/services">
                  Explore Services
                </Link>
              </Button>
            </div>
          </div>
          
          {/* Animation container with transparent background */}
          <div className="relative w-full max-w-md mx-auto my-12 h-[300px] sm:h-[320px] md:h-[340px] rounded-xl">
            {/* Enhanced category legend with improved spacing */}
            <div className="absolute top-2 left-0 right-0 flex justify-center z-40">
              <div className="bg-white/80 backdrop-blur-sm rounded-lg py-2 px-3 sm:px-4 shadow-sm flex flex-wrap justify-center sm:flex-nowrap items-center gap-3 sm:gap-5 md:gap-6 max-w-[95%] mx-auto">
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-pink-500"></div>
                  <span className="text-xs font-medium text-pink-700 whitespace-nowrap">Family</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-blue-500"></div>
                  <span className="text-xs font-medium text-blue-700 whitespace-nowrap">Professional</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-amber-500"></div>
                  <span className="text-xs font-medium text-amber-700 whitespace-nowrap">Volunteer</span>
                </div>
              </div>
            </div>
            
            <BubbleCareAnimation />
          </div>
          
          {/* Trust features */}
          <div className="flex flex-wrap justify-center gap-4 text-center">
            <div className="bg-white/80 backdrop-blur-sm rounded-lg px-6 py-3 shadow-sm">
              <p className="text-sm font-medium flex items-center justify-center">
                <Heart className="h-4 w-4 text-blue-500 mr-2" /> Trusted by 10,000+ families
              </p>
            </div>
            
            <div className="bg-white/80 backdrop-blur-sm rounded-lg px-6 py-3 shadow-sm">
              <p className="text-sm font-medium">Verified Caregivers</p>
            </div>
            
            <div className="bg-white/80 backdrop-blur-sm rounded-lg px-6 py-3 shadow-sm">
              <p className="text-sm font-medium">24/7 Support</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
